import { useDaten } from '../daten/kontext';
import type { Id, Datum } from '../daten/typen';
import { knotenPfad, dozentenLage } from '../daten/regeln';
import { tagLang, deLang, kurzeZeit } from '../daten/zeit';
import { useEffect, useState } from 'react';
import { Knopf, Kuerzel, Leer, dozentName, Icon } from '../ui/bausteine';

export function Absagen({ zuEinsatz }: { zuEinsatz: (datum: Datum, einsatzId: Id) => void }) {
  const { stand, quelle, ausfuehren } = useDaten();
  const offen = stand.absagen.filter(a => !a.erledigt_am).sort((a, b) => a.erstellt_am.localeCompare(b.erstellt_am));
  const erledigt = stand.absagen.filter(a => a.erledigt_am).sort((a, b) => (b.erledigt_am ?? '').localeCompare(a.erledigt_am ?? '')).slice(0, 15);
  const ohneDozent = stand.einsaetze.filter(e => !e.dozent_id && e.status === 'freigegeben' && !stand.absagen.some(a => a.einsatz_id === e.id && !a.erledigt_am));

  return (
    <div className="seite">
      <header className="seiten-kopf"><h1>Absagen und offene Termine</h1>
        <p className="leise">Hat ein Dozent abgesagt oder eine Abwesenheit eingetragen, steht der Termin hier, bis ein Ersatz eingeplant ist.</p></header>
      <PushKarte />
      {offen.length === 0 && ohneDozent.length === 0 && <Leer icon="haken" titel="Keine offenen Absagen" text="Alle freigegebenen Termine haben einen Dozenten." />}
      <div className="karten">
        {offen.map(a => {
          const e = stand.einsaetze.find(x => x.id === a.einsatz_id);
          if (!e) return null;
          const kurs = stand.kurse.find(k => k.id === e.kurs_id);
          const doz = stand.dozenten.find(d => d.id === a.dozent_id);
          const ersatz = dozentenLage(stand, e.kurs_id, e.kurs_knoten_id, e.datum, e.beginn, e.ende, e.id).filter(l => l.frei && l.qualifiziert);
          return (
            <article key={a.id} className="karte absage">
              <div className="karte-kopf"><Icon name="glocke" /><strong>{tagLang(e.datum)}, {deLang(e.datum)} · {kurzeZeit(e.beginn)}–{kurzeZeit(e.ende)}</strong></div>
              <div>{kurs?.kennung} · {knotenPfad(stand, e.kurs_knoten_id).slice(-2).map(k => k.bezeichnung).join(' · ') || 'ohne Thema'}</div>
              <div className="zeile">{doz && <Kuerzel text={doz.kuerzel} farbe={doz.farbe} klein />} {dozentName(doz)} – „{a.grund ?? 'ohne Angabe'}“</div>
              <div className="leise klein">gemeldet {new Date(a.erstellt_am).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}</div>
              <div className="zeile umbruch">
                <span className="klein">{ersatz.length} mögliche Vertretungen:</span>
                {ersatz.slice(0, 6).map(l => <Kuerzel key={l.dozent.id} text={l.dozent.kuerzel} farbe={l.dozent.farbe} klein />)}
              </div>
              <div className="knopfleiste">
                <Knopf art="primaer" icon="person" onClick={() => zuEinsatz(e.datum, e.id)}>Ersatz einplanen</Knopf>
                <Knopf art="leise" onClick={() => ausfuehren(() => quelle.absageErledigen(a.id), 'Als erledigt markiert')}>Erledigt</Knopf>
              </div>
            </article>
          );
        })}
        {ohneDozent.map(e => {
          const kurs = stand.kurse.find(k => k.id === e.kurs_id);
          return (
            <article key={e.id} className="karte">
              <div className="karte-kopf"><Icon name="warnung" /><strong>{tagLang(e.datum)}, {deLang(e.datum)} · {kurzeZeit(e.beginn)}–{kurzeZeit(e.ende)}</strong></div>
              <div>{kurs?.kennung} · freigegeben, aber ohne Dozent</div>
              <div className="knopfleiste"><Knopf icon="person" onClick={() => zuEinsatz(e.datum, e.id)}>Dozent einplanen</Knopf></div>
            </article>
          );
        })}
      </div>
      {erledigt.length > 0 && <>
        <h2 className="abschnitt">Zuletzt erledigt</h2>
        <ul className="liste-einfach">
          {erledigt.map(a => {
            const e = stand.einsaetze.find(x => x.id === a.einsatz_id);
            const doz = stand.dozenten.find(d => d.id === a.dozent_id);
            const neu = stand.dozenten.find(d => d.id === e?.dozent_id);
            return <li key={a.id}>{e ? `${deLang(e.datum)} ${kurzeZeit(e.beginn)}` : ''} · {doz?.kuerzel} abgesagt{neu ? ` → vertreten durch ${neu.kuerzel}` : ''}</li>;
          })}
        </ul>
      </>}
    </div>
  );
}

/** Push-Nachrichten bei Absagen für dieses Gerät */
function PushKarte() {
  const { quelle, ausfuehren, melden } = useDaten();
  const [status, setStatus] = useState<'aktiv' | 'aus' | 'nicht-moeglich' | 'demo'>(quelle.pushStatus ? 'aus' : 'demo');
  useEffect(() => { void quelle.pushStatus?.().then(setStatus); }, [quelle]);
  return (
    <div className={`hinweisbox ${status === 'aktiv' ? 'info' : 'warn'} push-karte`}>
      <Icon name="glocke" />
      <span>
        {status === 'aktiv' && 'Dieses Gerät erhält bei jeder neuen Absage eine Push-Nachricht.'}
        {status === 'aus' && 'Push-Nachrichten bei Absagen sind auf diesem Gerät noch nicht aktiviert.'}
        {status === 'nicht-moeglich' && 'Push-Nachrichten sind hier nicht möglich: Die Seite muss über https laufen und eingerichtet sein. Auf dem iPhone die Planung zuerst über „Teilen › Zum Home-Bildschirm“ als App ablegen und von dort öffnen.'}
        {status === 'demo' && 'Im Betrieb erhalten Administratoren bei jeder Absage sofort eine Push-Nachricht auf Handy oder PC. In der Demo ist diese Funktion abgeschaltet.'}
      </span>
      {status === 'aus' && <Knopf art="primaer" icon="glocke" onClick={async () => {
        let ergebnis: string = '';
        await ausfuehren(async () => { ergebnis = await quelle.pushAktivieren!(); });
        if (ergebnis === 'aktiv') { setStatus('aktiv'); melden('Push-Nachrichten aktiviert'); }
        else if (ergebnis === 'abgelehnt') melden('Die Berechtigung wurde im Browser abgelehnt. Sie lässt sich in den Website-Einstellungen wieder erlauben.', 'info');
      }}>Auf diesem Gerät aktivieren</Knopf>}
    </div>
  );
}
